import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Layout } from "react-grid-layout";

import {
  WIDGET_GRID_COLS,
  WIDGET_GRID_MARGIN,
  WIDGET_GRID_ROW_HEIGHT,
} from "@/features/shared/components/widget-grid-layout/widget-grid-layout";
import {
  applyGutterResize,
  createGutterResizeSession,
  discoverGutters,
  Gutter,
  GutterResizeSession,
} from "@/features/admin/features/pages-management/utils/gutters";
import {
  calcColumnWidth,
  pixelDeltaToUnits,
} from "@/features/admin/features/pages-management/utils/grid-resize-math";
import { isLayoutEqual } from "@/features/admin/features/pages-management/utils/is-layout-equal";

type Options = {
  layout: Layout[];
  getContainerWidth: () => number;
  onLayoutChange: (layout: Layout[]) => void;
  onInteractionStart?: () => void;
};

type PointerSession = {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  columnWidth: number;
  gutterSession: GutterResizeSession;
};

/**
 * Layout-derived gutter resize: drag a channel between abutting widget sets.
 */
export function useGutterResize({
  layout,
  getContainerWidth,
  onLayoutChange,
  onInteractionStart,
}: Options) {
  const [activeGutterId, setActiveGutterId] = useState<string | null>(null);
  const [squishIds, setSquishIds] = useState<ReadonlySet<string>>(() => new Set());
  const sessionRef = useRef<PointerSession | null>(null);
  const emittedRef = useRef<Layout[] | null>(null);

  const onLayoutChangeRef = useRef(onLayoutChange);
  onLayoutChangeRef.current = onLayoutChange;
  const onInteractionStartRef = useRef(onInteractionStart);
  onInteractionStartRef.current = onInteractionStart;

  const gutters = useMemo(() => discoverGutters(layout, WIDGET_GRID_COLS), [layout]);

  useEffect(() => {
    const [marginX, marginY] = WIDGET_GRID_MARGIN;

    const handlePointerMove = (event: PointerEvent) => {
      const session = sessionRef.current;
      if (!session || event.pointerId !== session.pointerId) return;

      const { gutterSession } = session;
      const { gutter } = gutterSession;

      const rawDelta =
        gutter.orientation === "vertical"
          ? pixelDeltaToUnits(
              event.clientX - session.startClientX,
              session.columnWidth,
              marginX,
            )
          : pixelDeltaToUnits(
              event.clientY - session.startClientY,
              WIDGET_GRID_ROW_HEIGHT,
              marginY,
            );

      const result = applyGutterResize(gutterSession, rawDelta);
      session.gutterSession = result.session;
      setSquishIds(result.squishIds);

      if (emittedRef.current && isLayoutEqual(result.layout, emittedRef.current)) return;
      emittedRef.current = result.layout;
      onLayoutChangeRef.current(result.layout);
    };

    const handlePointerUp = (event: PointerEvent) => {
      const session = sessionRef.current;
      if (!session || event.pointerId !== session.pointerId) return;
      sessionRef.current = null;
      emittedRef.current = null;
      setActiveGutterId(null);
      setSquishIds(new Set());
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, []);

  const startGutterResize = useCallback(
    (gutter: Gutter, event: React.PointerEvent<HTMLElement>) => {
      const containerWidth = getContainerWidth();
      if (containerWidth <= 0) return;

      event.preventDefault();
      event.stopPropagation();

      onInteractionStartRef.current?.();

      // Freeze the layout at pointer-down; later steps derive from this snapshot.
      const snapshot = layout;
      sessionRef.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        columnWidth: calcColumnWidth(
          containerWidth,
          WIDGET_GRID_COLS,
          WIDGET_GRID_MARGIN[0],
        ),
        gutterSession: createGutterResizeSession(snapshot, gutter),
      };
      emittedRef.current = snapshot;
      setActiveGutterId(gutter.id);
    },
    [getContainerWidth, layout],
  );

  return {
    gutters,
    activeGutterId,
    squishIds,
    startGutterResize,
    isResizing: activeGutterId !== null,
  };
}
